import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, setDoc, getDoc, updateDoc, query, where, getDocs, arrayUnion } from 'firebase/firestore';
import { LOCATIONS_BY_RAILROAD, SUB_YARDS, AGREEMENT_RULES, RAILROADS, COMMITTEE_LOOKUP, AGREEMENT_LOOKUP } from '../data/agreementData';
import { resolveAmtrakRule, canonicalizeAmtrakRules, onePerRule } from '../data/ruleCatalog';
import { resolveScopeKey } from '../data/scopeKeyData';
import { resolveLocalCommittee } from '../data/routing';
import { hasRuleCatalog } from '../data/ruleSourcing';
import { nextClaimNumber } from '../utils/claimNumber';
import { buildClaimPayload, computeAmountClaimed } from '../data/claimPayload';
import { pickResumableDraft, multipleDraftsNotice, draftIntoForm, droppedDraftKeys } from '../data/claimDrafts';
import { profilePatch, filerIsClaimant } from '../data/memberProfile';
import { claimPatch } from '../data/claimWrite';
import RulesMatcher from './RulesMatcher';
import ClaimNarrative from './ClaimNarrative';
import DictationButton from './DictationButton';
import EligibilityPayablePanel from '../components/EligibilityPayablePanel';
import HowToPanel from '../components/HowToPanel';
import SectionDialogs from '../components/SectionDialogs';
import { acknowledgePatch, clearOnEdit, sectionOfField, ackGate, ackRecord, ackSummary } from '../data/claimAck';   // Bob 2026-10-25: accept, or edit - and it is recorded   // Bob 2026-10-25: personal, employer and union as three pop-ups   // Bob 2026-10-25: what is expected of the member, on the screen he is filling in
import { whatHappensNow, filedHeadline } from '../data/claimNext';   // and what to do when it is complete
import ClaimBasisBoxes from '../components/ClaimBasisBoxes';
import ExhibitFileAttach from '../components/ExhibitFileAttach';
import { attachStagedExhibits } from '../data/claimDocumentClient';   // section 412 Part B
import { filerCopy, saveCopy, RULING_271_FILER_COPY } from '../data/filedCopy';   // ruling 271: the filer keeps a copy
import useSeatsOfRecord from '../hooks/useSeatsOfRecord';   // Record 609 (ruling 294): the Local Chairperson seat of record
import { recordCarriageAtFiling } from '../data/carriageAtFiling';   // Record 609 (ruling 294): carriage recorded at filing
import RuleBasisPicker from './RuleBasisPicker';
import { memberBasisLabel } from '../data/memberBasis';   // Record 612 (ruling 204): the fixed label on a member's suggested basis
import { ANALYZE_URL } from '../installation';   // ruling 312: the functions host comes from the installation
import { agreementFamily, profileDefaultAgreement } from '../data/agreementRegistry';   // ruling 312 phase 1 (d)
import { CRAFTS, DEFAULT_CRAFT, DIVISIONS, divisionOfCraft, craftsInDivision, wordsFor } from '../data/craftTerms';
import DivisionButtons from '../components/DivisionButtons';   // section 807   // ruling 312 phase 1 (c)


const VIOLATION_TYPES = [
  'Denied Seniority',
  'Denied Work / Passed Over',
  'Run-Around',
  'Illegal Job Swap',
  'Improper Assignment',
  'Working Out of Turn',
  'Improper Bulletin / Posting',
  'Improper Displacement',
  'Improper Furlough',
  'Failure to Call',
  'Improper Extra Board Operation',
  'Rate of Pay Dispute',
  'Rest Period Violation',
  'Improper Abolishment',
  'Jurisdiction Violation',
  'Other — Describe in Description',
];

const _today = new Date();
const TODAY_DATE = `${_today.getFullYear()}-${String(_today.getMonth()+1).padStart(2,'0')}-${String(_today.getDate()).padStart(2,'0')}`;
const TODAY_MONTH = String(_today.getMonth()+1).padStart(2,'0');
const TODAY_DAY = String(_today.getDate()).padStart(2,'0');
const TODAY_YEAR = String(_today.getFullYear());

export default function NewClaim({ user, onNavigate, draftClaimId, onDraftLoaded }) {
  const basisLabel = memberBasisLabel(user);   // Record 612: '' for anyone but a member

  const [dateError, setDateError] = useState('');
  const [openSection, setOpenSection] = useState(null);   // which of the three pop-ups is open
  const acknowledge = (sectionKey, action) => setForm(prev => ({
    ...prev, ...acknowledgePatch(sectionKey, action, new Date().toISOString(), (user && user.uid) || ''),
  }));
  const [division, setDivision] = useState(divisionOfCraft(user?.craft || ''));   // section 807: the division button
  const [form, setForm] = useState({
    claimantName: user?.name || '',
    rosterNumber: '',
    seniorityDate: '',
    employeeNumber: '',
    claimantJobSymbol: '',
    claimantTourOfDuty: '',
    shiftLength: '',
    shiftEnd: '',
    hosPosition: false,
    remedySought: '',
    craft: user?.craft || '',
    railroad: user?.railroad || '',
    workLocation: user?.workLocation || '',
    eventLocation: '',
generalCommittee: COMMITTEE_LOOKUP[user?.craft || '']?.[user?.railroad || ''] || '',
    localCommittee: resolveLocalCommittee(user?.craft || '', user?.railroad || '', user?.workLocation || ''),
    applicableAgreement: AGREEMENT_LOOKUP[COMMITTEE_LOOKUP[user?.craft || '']?.[user?.railroad || ''] || '']?.[user?.craft || '']?.[user?.railroad || ''] || '',
    violationType: '',
    violationTypeOther: '',
    rulesViolated: '',
    yardOfIncident: '',
    incidentDate: '',
    incidentTime12: '',
    incidentTime24: '',
    violatorName: '',
    violatorRosterNumber: '',
    violatorAssignment: '',
    violatorRestDays: '',
    violatorIsNonAgreement: false,
    subjectType: '', // 'yardmaster' | 'other_craft' | 'management'
    subjectCraftType: '',
    subjectSeniorityDate: '',
    majorDisputeFlag: false,
    personsInvolved: '',
    witnesses: '',
    description: '',
    hourlyRateST: '',
    hourlyRateOT: '',
    totalHoursClaimed: '',
    totalAmountST: '',
    totalAmountOT: '',
    isOvertimeClaim: true,
    priority: 'standard',
  });
  const W = wordsFor(form.craft || division);   // section 807: the words of the member's division

  const [submitted, setSubmitted] = useState(false);
  const [filedCopy, setFiledCopy] = useState(null);   // ruling 271: the filer's copy of the claim as filed
  // Record 609 (ruling 294): the furtherance submission recorded at filing, and the seats it is addressed by.
  const [filingCarriage, setFilingCarriage] = useState(null);
  const seatsAtFiling = useSeatsOfRecord(form.generalCommittee);
  const [exhibitFiles, setExhibitFiles] = useState([]);   // section 412 Part B: exhibit files staged, uploaded on submit
  const [nonAgreementRouted, setNonAgreementRouted] = useState(false);
  const [claimId, setClaimId] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [showDerived, setShowDerived] = useState(false);
  const [profileConfirmed, setProfileConfirmed] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [matchedRules, setMatchedRules] = useState([]);

  // Load draft claim data when resuming a draft
  React.useEffect(() => {
    if (!draftClaimId) return;
    getDoc(doc(db, 'claims', draftClaimId)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        // D-2, READ HALF. This was `{ ...prev, ...data }` - every key the stored document carried,
        // poured back into form state, including the four routing fields buildClaimPayload resolves
        // fresh so that a caller's bag can never pin a stale route. draftIntoForm admits only the
        // keys the form owns; the builder recomputes the rest on the next save.
        const refused = droppedDraftKeys(data);
        if (refused.length) console.debug('draft resume: not merged into form state:', refused);
        setForm(prev => ({ ...prev, ...draftIntoForm(data) }));
        setDraftId(draftClaimId);
        if (onDraftLoaded) onDraftLoaded();
      }
    }).catch(console.error);
  }, [draftClaimId]);

  // sync violationType with matchedRules
  React.useEffect(() => {
    if (matchedRules && matchedRules.length > 0) {
      const joined = matchedRules.map(r => r.rule).join(' \u2014 ');
      setForm(prev => prev.violationType === joined ? prev : { ...prev, violationType: joined });
    }
  }, [matchedRules]);

  // Auto-derive General Committee + Agreement from identifiers, however they're set (profile prefill, manual, draft)
  React.useEffect(() => {
    if (form.craft && form.railroad && !form.generalCommittee) {
      const gc = COMMITTEE_LOOKUP[form.craft]?.[form.railroad] || '';
      if (gc) {
        setForm(prev => ({ ...prev, generalCommittee: gc, applicableAgreement: prev.applicableAgreement || AGREEMENT_LOOKUP[gc]?.[form.craft]?.[form.railroad] || '' }));
      }
    }
  }, [form.craft, form.railroad, form.generalCommittee]);

  // Fetch user profile from Firestore to pre-fill form on mount
  React.useEffect(() => {
    if (!user?.uid) return;
    // RESUME THE MEMBER'S MOST RECENT DRAFT, AND SAY SO WHEN THERE ARE OTHERS.
    //
    // This used to be `limit(1)` with NO `orderBy`, so Firestore returned whichever draft it liked
    // and the next save wrote over it with no prompt. The query stays EQUALITY-ONLY on purpose -
    // adding an orderBy would need a composite index, ycp.cmd has no step that deploys one, and the
    // catch below would swallow the resulting error so draft adoption would silently stop working.
    // The ordering is decided in claimDrafts.js instead, where it is tested without Firestore.
    if (!draftClaimId) {
      getDocs(query(
        collection(db, 'claims'),
        where('createdBy', '==', user.uid),
        where('status', '==', 'draft')
      )).then(snap => {
        const { draft, others } = pickResumableDraft(snap.docs);
        if (draft) {
          // OFFERED, never poured in unasked (Bob 2026-10-14: "the date was here, I didnt enter it"). The form opens
          // empty; continueDraft() loads it on his word.
          const data = typeof draft.data === 'function' ? draft.data() : draft;
          setOfferedDraft({ id: draft.id, data, others });
        }
      }).catch((e) => { console.error('Draft lookup failed:', e); });
    }
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists()) {
        const p = snap.data();
        setForm(prev => ({
          ...prev,
          claimantName: prev.claimantName || p.name || user?.name || '',
          rosterNumber: prev.rosterNumber || p.rosterNumber || '',
          seniorityDate: prev.seniorityDate || p.seniorityDate || '',
          employeeNumber: prev.employeeNumber || p.employeeNumber || '',
          claimantJobSymbol: prev.claimantJobSymbol || p.claimantJobSymbol || '',
          claimantTourOfDuty: prev.claimantTourOfDuty || p.claimantTourOfDuty || '',
          claimantPhone: prev.claimantPhone || p.phone || '',
          claimantEmail: prev.claimantEmail || p.email || user?.email || '',
          claimantStreet: prev.claimantStreet || p.street || '',
          claimantCity: prev.claimantCity || p.city || '',
          claimantState: prev.claimantState || p.state || '',
          claimantZip: prev.claimantZip || p.zip || '',
          craft: prev.craft || p.craft || '',
          railroad: prev.railroad || p.railroad || '',
          workLocation: prev.workLocation || p.workLocation || '',
          generalCommittee: prev.generalCommittee || p.committee || user?.committee || '',
          localCommittee: prev.localCommittee
            || resolveLocalCommittee(p.craft || prev.craft, p.railroad || prev.railroad, p.workLocation)
            || resolveLocalCommittee(prev.craft || p.craft, prev.railroad || p.railroad, prev.workLocation)
            || '',
          applicableAgreement: prev.applicableAgreement || profileDefaultAgreement(p.railroad, p.craft || prev.craft) || profileDefaultAgreement(prev.railroad, prev.craft || p.craft) || '',   // ruling 312 phase 1; Record 723: Conrail, Soo, Metra added, Yardmaster craft only
          eventLocation: prev.eventLocation || p.workLocation || '',
          yardOfIncident: prev.yardOfIncident || p.workLocation || '',
        }));
      }
    }).catch(() => {});
  }, [user?.uid]);


  const [approvedNarrative, setApprovedNarrative] = useState('');

  // See the note at the requirements panel: matchedRules and the computed amount are visible
  // to the member but are not in `form`, so they are joined in here for requirement testing only.
  // eventLocation RESOLVES THE SAME WAY EVERY OTHER READER RESOLVES IT. The field initialises to ''
  // and five sites in this file already read it as `eventLocation || workLocation` - the select even
  // DISPLAYS that fallback while only writing eventLocation when the member actively changes it. So
  // a member who accepts the default has '' in the bag, and a requirement keyed on eventLocation
  // alone would show "Occurrence (date, location)" unmet while the location is on screen above it.
  // That was half a fix; this is the other half.
  const requirementsView = { ...form, matchedRules,
    eventLocation: form.eventLocation || form.workLocation || '',
    amountClaimed: computeAmountClaimed(form) || '' };



  const [analyzingRules, setAnalyzingRules] = useState(false);
  const [ruleAnalysisError, setRuleAnalysisError] = useState('');
  const [showClaimDetails, setShowClaimDetails] = useState(false);
  const [claimDetailQuestions, setClaimDetailQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const autoAnalyzeRules = async (description) => {
    // A silent return here is a lie: the box above promises "Applicable rules identify
    // automatically when you finish" and then nothing happens and nothing says why.
    // No description is the user's own doing and needs no message. A missing governing
    // agreement is the platform's state, not theirs, and must be said out loud.
    if (!description?.trim()) return;
    if (!form.applicableAgreement) {
      setRuleAnalysisError('Rule analysis needs the governing agreement, and none is set yet. Fill in craft, railroad and work location above - the agreement resolves from those, and the analysis will run.');
      return;
    }
    setAnalyzingRules(true);
    setRuleAnalysisError('');
    try {
      const availableRules = AGREEMENT_RULES[form.applicableAgreement] || [];
      const response = await fetch(ANALYZE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({ agreement: form.applicableAgreement, description, availableRules })
      });
      if (!response.ok) throw new Error('Server error');
      const data = await response.json();
      if (Array.isArray(data.rules) && data.rules.length > 0) {
       const _mapped = data.rules.map(r => { const n = (String(r.rule).match(/\d+/) || [])[0]; const a = availableRules.find(x => (String(x).match(/\d+/) || [])[0] === n); return a ? { rule: a, relevance: r.relevance || '' } : null; }).filter(Boolean);
       // §XI parity with QuickFile: for a catalogued (Amtrak) agreement, strip AI fullTitle/relevance
       // (and any exactLanguage) and canonicalize the label — no AI-authored text on a catalogued claim.
       setMatchedRules(agreementFamily(form.applicableAgreement) === 'amtrak-ym' ? canonicalizeAmtrakRules(_mapped) : _mapped.filter(onePerRule()));
      } else if (data.held === true) {
        // Ruling 90: the agreement is withheld from analysis; the function says why. Not a no-match.
        setRuleAnalysisError(data.heldReason || 'Rule analysis for this agreement is withheld until its rules have been verified. Choose the rule yourself from the rulebook.');
      } else {
        setRuleAnalysisError('No matching rules identified. Add more detail and save again.');
      }
    } catch (err) {
      setRuleAnalysisError('Rule analysis unavailable — check connection.');
    } finally {
      setAnalyzingRules(false);
    }
  };

  const fetchClaimDetailQuestions = async () => {
    if (!form.description?.trim() || matchedRules.length === 0) return;
    setLoadingQuestions(true);
    setShowClaimDetails(true);
    try {
      const response = await fetch(ANALYZE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({
          mode: 'questions',
          agreement: form.applicableAgreement,
          description: form.description,
          matchedRules: matchedRules.map(r => r.rule),
        })
      });
      const data = await response.json();
      if (Array.isArray(data.questions) && data.questions.length > 0) {
        setClaimDetailQuestions(data.questions);
      } else {
        setClaimDetailQuestions(['What was the exact date and time of the incident?', 'What is the name and roster number of the violating party?', 'Were there any witnesses present?', 'What was your availability status at the time?', 'Who authorized or made the improper assignment?']);
      }
    } catch (err) {
      setClaimDetailQuestions(['What was the exact date and time of the incident?', 'What is the name and roster number of the violating party?', 'Were there any witnesses present?', 'What was your availability status at the time?', 'Who authorized or made the improper assignment?']);
    } finally {
      setLoadingQuestions(false);
    }
  };
  const [error, setError] = useState('');

  const set = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      // Bob 2026-10-25: a confirmation is about the value that was on the screen when it was given, so
      // changing a field takes the confirmation off ITS OWN section and no other (claimAck.clearOnEdit).
      Object.assign(updated, clearOnEdit(prev, sectionOfField(field)));

      if (field === 'craft') {
        updated.railroad = ''; updated.workLocation = ''; updated.eventLocation = '';
        updated.generalCommittee = ''; updated.localCommittee = ''; updated.applicableAgreement = '';
        updated.yardOfIncident = '';
      }
      if (field === 'railroad') { updated.workLocation = ''; updated.eventLocation = ''; updated.yardOfIncident = ''; updated.localCommittee = ''; }
      if (field === 'workLocation') {
        const subs = SUB_YARDS[value] || [];
        updated.eventLocation = subs.length === 0 ? value : '';
        updated.yardOfIncident = subs.length === 0 ? value : subs.length === 1 ? subs[0] : '';
        updated.localCommittee = resolveLocalCommittee(updated.craft, updated.railroad, value);
      }

      if (field === 'eventLocation') {
        // Auto-populate yard of incident from work location + event location
        const workLoc = prev.workLocation || '';
        const isSubYard = value && value !== workLoc;
        updated.yardOfIncident = isSubYard ? `${workLoc} — ${value}` : workLoc;
      }

            if (field === 'railroad' || field === 'craft') {
        const craft = field === 'craft' ? value : prev.craft;
        const railroad = field === 'railroad' ? value : prev.railroad;
        if (craft && railroad) {
          const gc = COMMITTEE_LOOKUP[craft]?.[railroad] || '';
          updated.generalCommittee = gc;
          updated.applicableAgreement = AGREEMENT_LOOKUP[gc]?.[craft]?.[railroad] || '';
        }
      }

      if (field === 'incidentTime12') {
        // input is type="time" -> already 24-hour "HH:MM". Store as HHMM.
        updated.incidentTime24 = value ? value.replace(':', '') : '';
      }

      if (field === 'hourlyRateST') {
        const st = parseFloat(value) || 0;
        updated.hourlyRateOT = (st * 1.5).toFixed(2);
      }

      if (field === 'hourlyRateST' || field === 'totalHoursClaimed' || field === 'isOvertimeClaim') {
        const st = parseFloat(field === 'hourlyRateST' ? value : prev.hourlyRateST) || 0;
        const ot = st * 1.5;
        const hours = parseFloat(field === 'totalHoursClaimed' ? value : prev.totalHoursClaimed) || 0;
        const isOT = field === 'isOvertimeClaim' ? value : prev.isOvertimeClaim;
        updated.hourlyRateOT = ot.toFixed(2);
        if (isOT) {
          updated.totalAmountOT = (hours * ot).toFixed(2);
          updated.totalAmountST = '';
        } else {
          updated.totalAmountST = (hours * st).toFixed(2);
          updated.totalAmountOT = '';
        }
      }

      return updated;
    });
  };

  const setIncidentDateGuarded = (candidate) => {
    const [y, m, d] = candidate.split('-'); setDateError('');
    const _pad = (v) => String(v).padStart(2, "0"); const _norm = (y && m && d) ? `${y}-${_pad(m)}-${_pad(d)}` : ""; if (_norm && _norm > TODAY_DATE) {
      setDateError('Incident date cannot be in the future.');
        set('incidentDate', candidate);
        return;
    }
    setDateError('');
    set('incidentDate', candidate);
  };

  const subYards = form.workLocation ? (SUB_YARDS[form.workLocation] || []) : [];
  const hasSubYards = subYards.length > 0;
  const availableYards = form.workLocation ? (LOCATIONS_BY_RAILROAD[(form.craft ? form.craft + "\u241F" : "") + form.railroad] || LOCATIONS_BY_RAILROAD[form.railroad] || []) : [];
  // Incident Location options: the railroad's yards (a yardmaster can be mobile across desks),
  // always including the current yardOfIncident so composite/sub-yard values never render blank.
  const incidentOptions = (() => {
    const base = availableYards.length > 0 ? [...availableYards] : (form.workLocation ? [form.workLocation] : []);
    if (form.yardOfIncident && !base.includes(form.yardOfIncident)) base.unshift(form.yardOfIncident);
    return base;
  })();
  const hasSubYardsForWork = (SUB_YARDS[form.workLocation] || []).length > 0;
  const isIdentifiersComplete = form.craft && form.railroad && form.workLocation && (form.eventLocation || !hasSubYardsForWork);

  // Location-scoped rule advisories (Amtrak only): flag matched rules that are not-applicable or
  // deleted at the claim's location, and surface NY qualifiers — via the rule catalog resolver.
  const isAmtrakAgreement = agreementFamily(form.applicableAgreement) === 'amtrak-ym';   // ruling 312 phase 1 (d)
  const ruleAdvisories = (isAmtrakAgreement && matchedRules.length > 0 && form.workLocation)
    ? matchedRules.map(r => {
        const num = (String(r.rule).match(/\d+/) || [])[0];
        if (!num) return null;
        const eff = resolveAmtrakRule(num, form.workLocation, form.incidentDate || undefined);
        const problem = eff.status === 'not_applicable' || eff.status === 'deleted';
        if (!problem && (!eff.qualifiers || eff.qualifiers.length === 0)) return null;
        return { num, status: eff.status, qualifiers: eff.qualifiers || [], territory: eff.territory };
      }).filter(Boolean)
    : [];
  const isSubjectSelected = !!form.subjectType;
  const isPhase1Complete = (() => {
    const t = form.subjectType;
    if (!t) return false;
    if (t === 'management') return true;
    if (t === 'other_craft') return !!(form.subjectCraftType && form.violatorName);
    if (t === 'yardmaster') {
      const p = (form.subjectSeniorityDate || '').split('-');
      const dateOk = p.length === 3 && p.every(x => x !== '');
      return !!(form.violatorName && form.violatorRosterNumber && dateOk
                && form.violatorAssignment && form.violatorRestDays);
    }
    return false;
  })();
  const isFormValid = form.claimantName && isIdentifiersComplete && form.generalCommittee && isPhase1Complete &&
    form.violationType && form.incidentDate && form.incidentTime24 &&
    (form.totalAmountST || form.totalAmountOT) && form.remedySought && !dateError;

  const handleNonAgreementRoute = async () => {
    setSaving(true);
    const cn = await nextClaimNumber(form.generalCommittee); setClaimId(cn);
    try {
      await addDoc(collection(db, 'claims'), claimPatch(buildClaimPayload({
        form, uid: auth.currentUser?.uid || '', email: auth.currentUser?.email || '',
        profileConfirmed, matchedRules, approvedNarrative, sectionAcks: ackRecord(form),
        mode: 'submit', variant: 'nonAgreement', claimNumber: cn,
        serverTimestamp: serverTimestamp(), isNew: true,
      }), user || auth.currentUser));
      setNonAgreementRouted(true);
    } catch (err) {
      setError('Error routing claim: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const [submitError, setSubmitError] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftId, setDraftId] = useState(null);
  // Major Dispute auto-save outcome: '' | 'saving' | 'saved' | 'failed'. The screen says
  // "saved" only after the write succeeds (audit 2026-10-16, finding 1).
  const [mdSave, setMdSave] = useState('');
  const [mdSaveError, setMdSaveError] = useState('');
  const saveMajorDisputeDraft = async () => {
    setSavingDraft(true); setMdSave('saving'); setMdSaveError('');
    try {
      const mkMD = (isNew) => buildClaimPayload({
        form: { ...form, subjectType: 'management', majorDisputeFlag: true },
        uid: user?.uid || auth.currentUser?.uid || '', email: user?.email || '',
        profileConfirmed, matchedRules, approvedNarrative, sectionAcks: ackRecord(form),
        mode: 'draft', variant: 'majorDispute', serverTimestamp: serverTimestamp(), isNew,
      });
      if (draftId) { await updateDoc(doc(db, 'claims', draftId), claimPatch(mkMD(false), user || auth.currentUser)); }
      else { const ref = await addDoc(collection(db, 'claims'), claimPatch(mkMD(true), user || auth.currentUser)); setDraftId(ref.id); }
      setMdSave('saved');
    } catch (e) {
      console.error('Major dispute auto-save failed:', e);
      setMdSave('failed'); setMdSaveError((e && e.message) || 'unknown error');
    } finally { setSavingDraft(false); }
  };
  const [otherDraftCount, setOtherDraftCount] = useState(0);
  const [offeredDraft, setOfferedDraft] = useState(null);   // { id, data, others } - offered, loaded only on his word
  const continueDraft = () => {
    const { id, data, others } = offeredDraft || {};
    if (!id) return;
    // Section 487: load what the draft holds BEFORE binding its id. Binding the id alone let the
    // next "Save as Draft" write this form's contents over the member's saved draft.
    const refused = droppedDraftKeys(data);
    if (refused.length) console.debug('Full Form draft resume: not merged:', refused);
    setForm(prev => ({ ...prev, ...draftIntoForm(data) }));
    setDraftId(id);
    setOtherDraftCount(others);
    setOfferedDraft(null);
  };

  const handleSaveContact = async () => {
    // R-5: these are the CLAIMANT's details. Saving them into users/{uid} is only correct when the
    // signed-in person IS the claimant; an officer editing a member's contact block must not have it
    // written over his own record.
    if (!user?.uid || !filerIsClaimant({ filerEmail: user?.email, claimantEmail: form.claimantEmail })) { setEditingContact(false); return; }
    try {
      await setDoc(doc(db, 'users', user.uid), profilePatch({
        name: form.claimantName || '',
        street: form.claimantStreet || '',
        city: form.claimantCity || '',
        state: form.claimantState || '',
        zip: form.claimantZip || '',
        phone: form.claimantPhone || '',
        email: form.claimantEmail || '',
        rosterNumber: form.rosterNumber || '',
        seniorityDate: form.seniorityDate || '',
        craft: form.craft || '',
        railroad: form.railroad || '',
        workLocation: form.workLocation || '',
        committee: form.generalCommittee || '',
      }), { merge: true });
      setContactSaved(true);
      setEditingContact(false);
      setTimeout(() => setContactSaved(false), 3000);
    } catch (e) {
      console.error('Contact save failed:', e);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setDraftSaved(false);
    try {
      const mk = (isNew) => buildClaimPayload({
        form, uid: user?.uid || auth.currentUser?.uid || '', email: user?.email || '',
        profileConfirmed, matchedRules, approvedNarrative, sectionAcks: ackRecord(form),
        mode: 'draft', serverTimestamp: serverTimestamp(), isNew,
      });
      if (draftId) {
        await updateDoc(doc(db, 'claims', draftId), claimPatch(mk(false), user || auth.currentUser));
      } else {
        const ref = await addDoc(collection(db, 'claims'), claimPatch(mk(true), user || auth.currentUser));
        setDraftId(ref.id);
      }
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (e) {
      console.error('Draft save failed:', e);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    const missing = [];
    if (!form.claimantName) missing.push('Claimant Name');
    if (!form.railroad) missing.push('Railroad');
    if (!form.workLocation) missing.push('Work Location');
    if (!form.incidentDate) missing.push('Incident Date');
    if (!form.description?.trim()) missing.push('Description of Event');
    // Bob 2026-10-25: the claimant must answer each section before his claim is filed. It is HIS act,
    // so it is asked of a member filing his own claim - an officer filing for someone cannot confirm
    // another man's data on his behalf, and pretending otherwise would put a false confirmation on
    // the record.
    const isMemberFiling = !(user && (user.role === 'local_chair' || user.role === 'general_chair' || user.isAdmin));
    const gate = ackGate(form);
    if (isMemberFiling && !gate.ok && missing.length === 0) {
      setSubmitError(gate.text + ' Open each card at the top and confirm it.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (missing.length > 0) {
      setSubmitError('Please complete the following required fields: ' + missing.join(', '));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!isFormValid) { alert('Please complete all required fields before filing.'); return; }
    setSaving(true); setError('');
    const cn = await nextClaimNumber(form.generalCommittee); setClaimId(cn);
    try {
      // Ruling 271: the payload is held so the filer's copy is drawn from exactly what was written.
      // (The write itself stays wrapped in claimPatch() on its own line - AUD-2.)
      const filedPayload = buildClaimPayload({
        form, uid: auth.currentUser?.uid || '', email: auth.currentUser?.email || '',
        profileConfirmed, matchedRules, approvedNarrative, sectionAcks: ackRecord(form),
        mode: 'submit', claimNumber: cn,
        serverTimestamp: serverTimestamp(), isNew: true,
      });
      const claimRef = await addDoc(collection(db, 'claims'), claimPatch(filedPayload, user || auth.currentUser));
      // Record 609 (ruling 294): filed - the furtherance submission to the Local Chairperson of record is recorded by
      // default, once. It never blocks the filing: a refusal comes back as a line on the success screen, not a throw.
      setFilingCarriage(await recordCarriageAtFiling({ claimId: claimRef && claimRef.id, claim: filedPayload,
        filerUid: auth.currentUser?.uid || '', roster: seatsAtFiling.roster, rosterSource: seatsAtFiling.source }));

      // SECTION 412 PART B: attach the exhibit files staged above, now that the claim has an id. Each
      // goes through the claimDocumentUploadUrl callable. A per-file failure - or a claim not yet routed
      // to a Local - does not lose the claim; it is reported so it can be attached later from the claim.
      if (exhibitFiles.length && claimRef?.id) {
        const { entries, errors } = await attachStagedExhibits({ claimId: claimRef.id, exhibitFiles, user });
        if (entries.length) {
          await updateDoc(doc(db, 'claims', claimRef.id), claimPatch({ documents: arrayUnion(...entries), updatedAt: serverTimestamp() }, user || auth.currentUser));
        }
        if (errors.length) {
          alert('Your claim was filed. ' + entries.length + ' of ' + exhibitFiles.length + ' exhibit file(s) attached. These could not be attached and can be added from the claim\'s Documents panel:\n\n' + errors.join('\n'));
        }
      }

      if (!resolveScopeKey(form.craft, form.railroad, form.workLocation)) {
        // CORRECTED 2026-09-22 (section 364, audit finding D5). This used to say 'your filing deadline
        // is protected'. THE PLATFORM CANNOT KNOW THAT. Whether a filing was timely is decided under the
        // agreement by the officers who hold it, and this line was fired on the path where routing had
        // just FAILED - so the platform was promising a legal consequence at the moment it knew least.
        // It now states what happened and nothing else.
        alert('Your claim has been filed. Your work location is not yet set up in the system, so it has been routed to your General Chairperson to assign to the correct Local Chairperson. Nothing on this platform decides whether a filing was on time - that is decided under your agreement.');
      }
      // Save profile for future pre-fill - R-5: only when the filer is the claimant.
      if (user?.uid && filerIsClaimant({ filerEmail: user?.email, claimantEmail: form.claimantEmail })) {
        await setDoc(doc(db, 'users', user.uid), profilePatch({
          craft: form.craft,
          railroad: form.railroad,
          workLocation: form.workLocation,
          name: form.claimantName,
          rosterNumber: form.rosterNumber,
          seniorityDate: form.seniorityDate,
          employeeNumber: form.employeeNumber,
          claimantJobSymbol: form.claimantJobSymbol,
        hosPosition: form.hosPosition || false,
          claimantTourOfDuty: form.claimantTourOfDuty,
          email: form.claimantEmail,
          phone: form.claimantPhone,
        }), { merge: true });
      }
      // RULING 271 (2026-10-02): on submit, the filer is given the claim as filed - every page - to keep. Drawn from the
      // record just written; the platform's own record is untouched. A copy that cannot be drawn never blocks the
      // filing: the success screen says so and the claim page's Download PDF remains.
      if (RULING_271_FILER_COPY) {
        const copy = await filerCopy({ ...filedPayload, id: cn }, user);
        if (copy.ok) saveCopy(copy);
        setFiledCopy(copy);
      }
      setSubmitted(true);
    } catch (err) {
      console.error('CLAIM WRITE FAILED');
      console.error('  code:', err.code);
      console.error('  message:', err.message);
      console.error('  full:', err);
      setError('Error saving claim: ' + err.message + (err.code ? ' [' + err.code + ']' : ''));
    } finally {
      setSaving(false);
    }
  };

  if (nonAgreementRouted) {
    return (
      <div>
        <div className="topbar"><h1 className="topbar-title">Non-Agreement Employee — Routed</h1></div>
        <div className="page-content">
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📨</div>
            <h2 style={{ color: '#E8A020', marginBottom: 8 }}>Letter of Non-Acquiescence Generated</h2>
            <p style={{ color: '#8AA4BF', marginBottom: 8 }}>This claim involves a non-agreement employee. A Letter of Non-Acquiescence has been generated and routed to:</p>
            <p style={{ color: '#F4F6F8', fontWeight: 700, marginBottom: 24 }}>Local Chairperson &amp; General Chairperson — {form.generalCommittee}</p>
            <div style={{ background: '#0D1B2A', border: '1px solid #E8A020', borderRadius: 8, padding: '16px 32px', display: 'inline-block', marginBottom: 32 }}>
              <div style={{ fontSize: 11, color: '#8AA4BF', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Reference Number</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#E8A020', fontFamily: 'monospace' }}>{claimId}</div>
            </div>
            <div>
              <button className="btn btn-primary" onClick={() => onNavigate('claims')}>View All Claims</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div>
        <div className="topbar"><h1 className="topbar-title">Claim Filed</h1></div>
        <div className="page-content">
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h2 style={{ color: '#27AE60', marginBottom: 8 }}>Claim Successfully Filed</h2>
            {/* Bob 2026-10-25: "submitted and saved to the platform database" is a developer's sentence.
                What a man needs to know is who has his claim and what happens next - projected from his
                own agreement by src\data\claimNext.js, which invents nothing. */}
            <p style={{ color: '#DCE7F2', marginBottom: 20, fontSize: 14, lineHeight: 1.6, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
              {filedHeadline(form.applicableAgreement, claimId)}
            </p>
            <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '16px 32px', display: 'inline-block', marginBottom: 32 }}>
              <div style={{ fontSize: 11, color: '#8AA4BF', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Claim Number</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#E8A020', fontFamily: 'monospace' }}>{claimId}</div>
            </div>
            {/* RULING 271: the filer's copy of what was filed, all pages, to keep. */}
            {RULING_271_FILER_COPY && filedCopy && (
              <div style={{ fontSize: 12, color: filedCopy.ok ? '#DCE7F2' : '#E8A020', marginBottom: 20, lineHeight: 1.6 }}>
                {filedCopy.ok ? (
                  <>Your copy of the claim as filed ({filedCopy.pages} page{filedCopy.pages === 1 ? '' : 's'}) has been saved to your device. Keep it.{' '}
                    <a href={filedCopy.url} download={filedCopy.fileName} style={{ color: '#E8A020' }}>Save it again</a>
                    {!filedCopy.sendable ? <div style={{ color: '#8AA4BF' }}>It is marked PREVIEW because the form is not complete yet - exactly as the claim page shows it.</div> : null}</>
                ) : (
                  <>Your copy could not be drawn here ({filedCopy.error || 'unknown reason'}). The claim is filed; download it from the claim page.</>
                )}
              </div>
            )}
            {/* Record 609 (ruling 294): carriage recorded at filing - or why it was not. */}
            {filingCarriage && filingCarriage.line && (
              <div style={{ fontSize: 12, color: filingCarriage.ok ? '#27AE60' : '#E8A020', marginBottom: 20, lineHeight: 1.6 }}>{filingCarriage.line}</div>
            )}
            {(() => {
              const next = whatHappensNow(form.applicableAgreement, { committee: form.generalCommittee });
              return (
                <div style={{ textAlign: 'left', maxWidth: 720, margin: '0 auto 28px' }}>
                  <h2 style={{ fontSize: 16, color: '#F4F6F8', margin: '0 0 8px' }}>What happens now</h2>
                  {!next.recorded ? (
                    <div style={{ fontSize: 13, color: '#FFD27A', lineHeight: 1.6 }}>{next.text}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 11.5, color: '#C08A2E', marginBottom: 10 }}>{next.unconfirmed}</div>
                      <ol style={{ margin: 0, paddingLeft: 20, color: '#DCE7F2', fontSize: 13, lineHeight: 1.65 }}>
                        {next.steps.map((st) => (
                          <li key={st.n} style={{ marginBottom: 10 }}>
                            <b style={{ color: '#F4F6F8' }}>{st.title}</b>
                            {st.cite ? <span style={{ color: '#8AA4BF', fontSize: 11 }}>  [{st.cite}]</span> : null}
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, color: '#A8C4DF' }}>
                              {st.body.map((b, i) => <li key={i} style={{ marginBottom: 2 }}>{b}</li>)}
                            </ul>
                          </li>
                        ))}
                      </ol>
                    </>
                  )}
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={() => onNavigate('claims')}>View All Claims</button>
              <button className="btn btn-secondary" onClick={() => { setSubmitted(false); setError(''); setFiledCopy(null); setFilingCarriage(null); }}>File Another</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="topbar-title">New Claim</h1>
          <div className="topbar-meta">All required fields must be completed before filing</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-secondary" onClick={() => {
            if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost. Use Save as Draft to keep your work.')) {
              onNavigate('claims');
            }
          }}>Cancel</button>
        </div>
      </div>

      <div className="page-content">
        {/* Bob 2026-10-25: what is expected of the member, on the screen he is filling in - and OPEN for
            a member, because the steps he has to follow are not something to hide behind a toggle. */}
        {/* Bob 2026-10-25: three pop-ups instead of one long scroll. Shown to a MEMBER; an officer
            filing for someone keeps the long form, which is faster when you do it all day. The fields
            are the same fields and the same setter - there is no second form (srcdataclaimSections.js). */}
        {!(user && (user.role === 'local_chair' || user.role === 'general_chair' || user.isAdmin)) && (
          <SectionDialogs form={form} set={set} onAck={acknowledge} openKey={openSection}
            onOpen={setOpenSection} onClose={() => setOpenSection(null)} summary={ackSummary(form)} />
        )}
        <HowToPanel kind="claim" agreement={form.applicableAgreement}
          defaultOpen={!(user && (user.role === 'local_chair' || user.role === 'general_chair' || user.isAdmin))} />
        {error && <div style={{ background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#FF6B6B' }}>⚠ {error}</div>}
        {offeredDraft && !draftId && (
          <div style={{ background: '#13263D', border: '1px solid #2E4A6B', borderRadius: 4,
                        padding: '8px 12px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>You have a claim you started and saved{offeredDraft.data && offeredDraft.data.updatedAt && typeof offeredDraft.data.updatedAt.toDate === 'function' ? ' on ' + offeredDraft.data.updatedAt.toDate().toLocaleDateString() : ''}. This form is empty - it is a new claim.</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={continueDraft}>Continue that draft instead</button>
          </div>
        )}

        {/* The member is TOLD when more than one draft exists. Binding the newest silently was
            still silent, and silence is what let the old code overwrite an arbitrary draft. */}
        {otherDraftCount > 0 && (
          <div style={{ background: '#12283D', border: '1px solid #2F5B85', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#9CC6EE', fontSize: 13 }}>
            {multipleDraftsNotice(otherDraftCount)}
          </div>
        )}

        {/* MODULE 1 — INTAKE */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Intake — Claimant &amp; Jurisdiction</div>
              <div className="card-subtitle">Complete all identifiers. General Committee and Agreement auto-populate.</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Claimant Name *</label>
            <input aria-label="Claimant Name" className="form-input" style={{ fontSize: 15, fontWeight: 700, color: '#F4F6F8' }} placeholder="Full name of claimant" value={form.claimantName} onChange={e => set('claimantName', e.target.value)} />
          </div>

          <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editingContact ? 10 : 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#8AA4BF' }}>Claimant Contact Information</div>
              {!editingContact && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {contactSaved && <span style={{ fontSize: 11, color: '#40C070', fontWeight: 700 }}>✓ Saved</span>}
                  <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '6px 16px', fontWeight: 700, background: '#0D3B26', borderColor: '#40C070', color: '#40C070' }} onClick={handleSaveContact}>✓ Accept</button>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 16px', fontWeight: 700 }} onClick={() => setEditingContact(true)}>✏️ Edit</button>
                </div>
              )}
              {editingContact && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '6px 16px', fontWeight: 700, background: '#0D3B26', borderColor: '#40C070', color: '#40C070' }} onClick={handleSaveContact}>Save</button>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 16px' }} onClick={() => setEditingContact(false)}>Cancel</button>
                </div>
              )}
            </div>
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                <div>
                  <span style={{ fontSize: 11, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>Address </span>
                  <div style={{ fontSize: 13, fontWeight: 600, color: form.claimantStreet ? '#E8F0FB' : '#4A6280' }}>
                    {form.claimantStreet ? `${form.claimantStreet}${form.claimantCity ? ', ' + form.claimantCity : ''}${form.claimantState ? ', ' + form.claimantState : ''}${form.claimantZip ? ' ' + form.claimantZip : ''}` : 'Not entered'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 11, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>Cell </span>
                  <div style={{ fontSize: 13, fontWeight: 600, color: form.claimantPhone ? '#E8F0FB' : '#4A6280' }}>{form.claimantPhone || 'Not entered'}</div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={{ fontSize: 11, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>Email </span>
                  <div style={{ fontSize: 13, fontWeight: 600, color: form.claimantEmail ? '#E8F0FB' : '#4A6280' }}>{form.claimantEmail || 'Not entered'}</div>
                </div>
              </div>
            </div>
            {editingContact && (
              <div>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label className="form-label" style={{ fontSize: 10 }}>Street Address</label>
                  <input aria-label="Street Address" className="form-input" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="Street address" value={form.claimantStreet} onChange={e => set('claimantStreet', e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 2 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>City</label>
                    <input aria-label="City" className="form-input" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="City" value={form.claimantCity} onChange={e => set('claimantCity', e.target.value)} />
                  </div>
                  <div style={{ flex: 0, minWidth: 60 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>State</label>
                    <input aria-label="State" className="form-input" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="NY" maxLength={2} value={form.claimantState} onChange={e => set('claimantState', e.target.value.toUpperCase())} />
                  </div>
                  <div style={{ flex: 1, minWidth: 80 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>ZIP</label>
                    <input aria-label="ZIP" className="form-input" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="12345" maxLength={10} value={form.claimantZip} onChange={e => set('claimantZip', e.target.value)} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>Cell Phone</label>
                    <input aria-label="Cell Phone" className="form-input" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="(555) 555-5555" value={form.claimantPhone} onChange={e => set('claimantPhone', e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>Email Address</label>
                    <input aria-label="Email Address" className="form-input" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="email@example.com" value={form.claimantEmail} onChange={e => set('claimantEmail', e.target.value)} />
                  </div>
                </div>
              </div>
            )}
          </div>

            <div style={{ background: '#0A1628', border: '1px solid ' + (profileConfirmed ? '#2E6B4A' : '#E8A020'), borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              {!profileConfirmed && (
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#E8A020', marginBottom: 8 }}>
                  ⚠ Confirm your information is current
                </div>
              )}
              <div style={{ fontSize: 13, color: '#F4F6F8', marginBottom: 10 }}>
                {[form.claimantName, form.craft, form.railroad, form.workLocation].filter(Boolean).join(' · ') || 'Complete your identifiers below'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!profileConfirmed && (
                  <button type="button" className="btn btn-primary"
                    style={{ fontSize: 12, padding: '6px 14px', background: '#0D3B26', borderColor: '#40C070', color: '#40C070' }}
                    onClick={() => { setProfileConfirmed(true); setShowProfile(false); }}>
                    ✓ Yes, correct
                  </button>
                )}
                <button type="button" className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '6px 14px' }}
                  onClick={() => setShowProfile(!showProfile)}>
                  {showProfile ? '▾ Hide details' : '✎ Needs updating'}
                </button>
              </div>
            </div>
            {showProfile && (<>
          {/* Claimant Identifiers — Roster, Seniority, Employee #, Job Symbol, Tour */}
          <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 8, padding: '14px 16px', marginTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: '#8AA4BF', textTransform: 'uppercase', marginBottom: 12 }}>
              Personal Identifiers — Roster & Seniority
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="form-label">Roster Number</label>
                <input aria-label="Roster Number" className="form-input" placeholder="e.g. 4421" value={form.rosterNumber || ''} onChange={e => set('rosterNumber', e.target.value)} />
                <div style={{ fontSize: 10, color: '#8AA4BF', marginTop: 2 }}>May change — verify before filing</div>
              </div>
              <div>
                <label className="form-label">Seniority Date</label>
                <input aria-label="Seniority Date" className="form-input" type="date" min="1975-01-01" max={TODAY_DATE} value={form.seniorityDate || ''} onChange={e => set('seniorityDate', e.target.value)} />
                <div style={{ fontSize: 10, color: '#8AA4BF', marginTop: 2 }}>Static unless contested and awarded</div>
              </div>
            </div>
          </div>
          <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '18px 20px', marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#E8A020', marginBottom: 14 }}>Required Identifiers</div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">1. Craft *</label>
                <DivisionButtons value={division} onPick={(d) => { setDivision(d); set('craft', ''); }} />
                <select aria-label="1. Craft" className="form-select" value={form.craft} onChange={e => set('craft', e.target.value)} disabled={DIVISIONS.length > 1 && !division}>
                  <option value="">{DIVISIONS.length > 1 && !division ? 'Choose your division first' : 'Select craft...'}</option>
                  {craftsInDivision(division, CRAFTS).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">2. Railroad (Employer) *</label>
                <select aria-label="2. Railroad (Employer)" className="form-select" value={form.railroad} onChange={e => set('railroad', e.target.value)} disabled={!form.craft}>
                  <option value="">{form.craft ? 'Select railroad...' : 'Select craft first'}</option>
                  {form.craft && RAILROADS[form.craft]?.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{W.workLocationLabel}</label>
              <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>{W.workLocationHelp}</div>
              <select aria-label="Work location" className="form-select" value={form.workLocation} onChange={e => set('workLocation', e.target.value)} disabled={!form.railroad}>
                <option value="">{!form.railroad ? 'Select railroad first' : 'Select work location...'}</option>
                {(LOCATIONS_BY_RAILROAD[(form.craft ? form.craft + "\u241F" : "") + form.railroad] || LOCATIONS_BY_RAILROAD[form.railroad] || []).map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>

            {W.jobNumberLabel && (
              <div className="form-group">
                <label className="form-label">{W.jobNumberLabel}</label>
                <input aria-label="Job number" className="form-input" value={form.jobNumber || ''} onChange={e => set('jobNumber', e.target.value)} />
              </div>
            )}

            {form.workLocation && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{W.eventLocationLabel}</label>
                <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 8 }}>
                  {hasSubYards ? W.eventLocationPick : W.eventLocationNone}
                </div>
                {hasSubYards ? (
                  <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '12px 14px' }}>
                    {subYards.map(yard => (
                      <label key={yard} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer' }}>
                        <input aria-label={yard} type="radio" name="eventLocation" value={yard} checked={form.eventLocation === yard} onChange={e => set('eventLocation', e.target.value)} style={{ accentColor: '#E8A020' }} />
                        <span style={{ fontSize: 13, color: '#F4F6F8' }}>{yard}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '10px 14px', background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: (form.eventLocation || form.workLocation) ? '#F4F6F8' : '#4A6280' }}>{form.eventLocation || form.workLocation}</span>

                  </div>
                )}
              </div>
            )}
          </div>
            </>)}

          <div style={{ marginBottom: 12 }}>
              <button type="button" onClick={() => setShowDerived(!showDerived)}
                style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 11,
                         letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer', padding: 0 }}>
                {showDerived ? '\u25BE Hide committee & agreement' : '\u25B8 Show committee & agreement'}
              </button>
            </div>
            {showDerived && (<>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">General Committee</label>
              <div style={{ padding: '10px 14px', background: '#0D1B2A', border: `1px solid ${form.generalCommittee ? '#E8A020' : '#2E4A6B'}`, borderRadius: 6, fontSize: 14, color: form.generalCommittee ? '#E8A020' : '#4A6280', fontWeight: form.generalCommittee ? 700 : 400, minHeight: 42 }}>
                {form.generalCommittee || 'Auto-populates from identifiers'}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Local Committee</label>
              <input aria-label="Local Committee" className="form-input" placeholder="Auto-populates from work location" readOnly={!!form.localCommittee} style={{ background: form.localCommittee ? '#0D1B2A' : undefined, color: form.localCommittee ? '#E8A020' : undefined, fontWeight: form.localCommittee ? 700 : undefined }} value={form.localCommittee} onChange={e => set('localCommittee', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Applicable Agreement</label>
            <div style={{ padding: '10px 14px', background: '#0D1B2A', border: `1px solid ${form.applicableAgreement ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, fontSize: 13, color: form.applicableAgreement ? '#40C070' : '#4A6280', minHeight: 42 }}>
              {form.applicableAgreement || (form.generalCommittee ? 'Agreement pending sourcing — contact administrator' : 'Auto-populates from identifiers')}
            </div>
          </div>
            </>)}
        </div>

        {/* The panel is handed a VIEW of the bag, not the bag: matchedRules and the computed
            amount are on screen but live outside form state, so a requirement keyed on either
            could never tick no matter what the member did. amountClaimed is blanked when zero -
            String(0) is truthy and a zero remedy is not a stated remedy. */}
        <EligibilityPayablePanel agreement={form.applicableAgreement} form={requirementsView} />

        {/* PHASE 1 — CARD 2: WHAT HAPPENED */}
        <div className="card" style={{ opacity: isIdentifiersComplete ? 1 : 0.5, pointerEvents: isIdentifiersComplete ? 'auto' : 'none' }}>
          <div className="card-header">
            <div>
              <div className="card-title">What Happened</div>
              <div className="card-subtitle" style={{ color: '#E8A020', fontSize: 11, fontWeight: 600 }}>
                PHASE 1 — Complete now to file your claim
              </div>
            </div>
          </div>

            {/* Major Dispute Banner */}
          {(form.majorDisputeFlag || form.subjectType === 'management') && (
            <div style={{ background: '#2D0A0A', border: '2px solid #C0392B', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: '#FF6B6B', fontWeight: 700, marginBottom: 4 }}>⚠ POSSIBLE MAJOR DISPUTE DETECTED</div>
              <div style={{ fontSize: 12, color: '#F4A0A0', lineHeight: 1.6 }}>
                This claim may involve a management or non-agreement action. That can be a Major Dispute under the Railway Labor Act. <strong>Only the General Chairperson ({form.generalCommittee || 'your committee'}) has authority to proceed with a Major Dispute.</strong> This platform has notified no one. Contact your Local Chairperson yourself, now. Your filing deadline still runs.
              </div>
            </div>
          )}

          {/* Description */}
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Description of Event *</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {form.majorDisputeFlag && (<span style={{ fontSize: 11, color: '#FF6B6B', fontWeight: 700 }}>MAJOR DISPUTE</span>)}
                <DictationButton onText={t => setForm(prev => ({ ...prev, description: (prev.description ? prev.description.trim() + ' ' : '') + t }))} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
              Describe what happened. Rules will be identified automatically when you tab out.
            </div>
            <textarea aria-label="What happened, in your own words"
              className="form-textarea"
              placeholder={W.newClaimDescriptionExample}
              value={form.description}
              onChange={e => {
                set('description', e.target.value);
                const keywords = ['management', 'supervisor', 'manager', 'foreman', 'non-agreement', 'carrier agent', 'directed by', 'ordered by', 'forced by'];
                const lower = e.target.value.toLowerCase();
                if (keywords.some(k => lower.includes(k))) set('majorDisputeFlag', true);
              }}
              onBlur={e => autoAnalyzeRules(e.target.value)}
              rows={4}
            />
          </div>

          {/* Date + Time + Location — all on one row */}
          <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Incident Date *</label>
              <input aria-label="Incident Date" className="form-input" type="date" min="1975-01-01" max={TODAY_DATE} value={form.incidentDate || ''} onChange={e => setIncidentDateGuarded(e.target.value)} />
              {dateError && <div style={{ fontSize: 11, color: '#FF6B6B', marginTop: 4 }}>⚠ {dateError}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Time of Incident *</label>
              <input aria-label="Time of Incident" className="form-input" type="time" value={form.incidentTime12} onChange={e => set('incidentTime12', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Incident Location</label>
              <select aria-label="Incident Location" className="form-select" value={form.yardOfIncident} onChange={e => set('yardOfIncident', e.target.value)}>
                <option value="">Select location...</option>
                {incidentOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Claim Narrative — inline after description */}
          {form.description && form.description.length > 20 && (
            <ClaimNarrative
              description={form.description}
              matchedRules={matchedRules}
              railroad={form.railroad}
              agreement={form.applicableAgreement}
              user={user}
              onNarrativeSaved={(text) => setApprovedNarrative(text)}
              formData={form}
            />
          )}

          {/* Analyzing / Rule Results */}
          {analyzingRules && (
            <div style={{ padding: '10px 14px', background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 6, marginBottom: 12, color: '#8AA4BF', fontSize: 12 }}>
              ⚙ Analyzing description against {form.applicableAgreement}...
            </div>
          )}
          {ruleAnalysisError && !analyzingRules && (
            <div style={{ padding: '8px 12px', background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 6, marginBottom: 12, color: '#FF6B6B', fontSize: 12 }}>⚠ {ruleAnalysisError}</div>
          )}
          {matchedRules.length > 0 && !analyzingRules && (
              <div style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '8px 14px', marginBottom: 16, fontSize: 12, color: '#40C070' }}>
                ✓ Rules identified from your description. You will review them before filing.
                {basisLabel ? <div style={{ fontSize: 11, color: '#FFD27A', marginTop: 4 }}>{basisLabel}</div> : null}
              </div>
            )}
          {ruleAdvisories.length > 0 && !analyzingRules && (
            <div style={{ marginBottom: 16 }}>
              {ruleAdvisories.map(a => {
                const isProblem = a.status === 'not_applicable' || a.status === 'deleted';
                return (
                  <div key={a.num} style={{ fontSize: 12, padding: '8px 12px', marginBottom: 6, borderRadius: 6,
                    background: isProblem ? '#2D0A0A' : '#0D1B2A',
                    border: `1px solid ${isProblem ? '#C0392B' : '#2E4A6B'}`,
                    color: isProblem ? '#FF6B6B' : '#8AA4BF' }}>
                    {isProblem
                      ? <><b>⚠ Rule {a.num} is {a.status.replace('_', ' ')} at {a.territory || 'this location'}</b> — it should not be cited here.{a.qualifiers.length ? ' ' + a.qualifiers.join(' ') : ''}</>
                      : <>ℹ {a.qualifiers.join(' ')}</>}
                  </div>
                );
              })}
            </div>
          )}

            {/* Rule / basis of claim */}
          {hasRuleCatalog(form.applicableAgreement) ? (
            <RuleBasisPicker
              agreement={form.applicableAgreement}
              location={form.yardOfIncident || form.eventLocation}
              asOfDate={form.incidentDate}
              matchedRules={matchedRules}
              onChange={setMatchedRules}
              analyzing={analyzingRules}
              memberLabel={basisLabel}
            />
          ) : (
          <div className="form-group">
            <label className="form-label">Violation Type</label>
            {matchedRules.length > 0 ? (
              <div style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, color: '#40C070', marginBottom: 4 }}>&#x2713; Auto-identified from description</div>
                {basisLabel ? <div style={{ fontSize: 11, color: '#FFD27A', marginBottom: 4 }}>{basisLabel}</div> : null}
                <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{matchedRules.map(r => r.rule).join(' — ')}</div>
              </div>
            ) : (
              <select aria-label="Violation type" className="form-select" value={form.violationType} onChange={e => set('violationType', e.target.value)}>
                <option value="">Select violation type or describe event above...</option>
                {VIOLATION_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
          </div>
          )}
        </div>

        <ClaimBasisBoxes agreement={form.applicableAgreement} form={requirementsView} onSet={set} />

        {/* SECTION 412 PART B — EXHIBITS & EVIDENCE. Attach the actual files where the claim asks for
            them; they upload securely (through the claimDocumentUploadUrl callable) when the claim is
            filed and appear under the claim's Documents & Exhibits. */}
        <div className="card" style={{ borderLeft: '4px solid #2E6B4A' }}>
          <div className="card-header">
            <div>
              <div className="card-title">Exhibits &amp; Evidence</div>
              <div className="card-subtitle" style={{ color: '#E8A020', fontSize: 11, fontWeight: 600 }}>
                Attach the documents that support this claim — timesheets, letters, awards, photos.
              </div>
            </div>
          </div>
          <ExhibitFileAttach files={exhibitFiles} onChange={setExhibitFiles} disabled={saving} />
        </div>

        {/* PHASE 1 — CARD 3: WHO DID IT */}
        <div className="card" style={{ opacity: isIdentifiersComplete ? 1 : 0.5, pointerEvents: isIdentifiersComplete ? 'auto' : 'none' }}>
          <div className="card-header">
            <div>
              <div className="card-title">Subject of Claim & Parties Involved</div>
              <div className="card-subtitle" style={{ color: '#E8A020', fontSize: 11, fontWeight: 600 }}>PHASE 1 — Identify who is responsible</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ color: !form.subjectType ? '#FF6B6B' : undefined }}>Subject of Claim *</label>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>Select the type of employee or party responsible. Select only one.</div>
            <select aria-label="Subject of Claim" className="form-select" style={{ borderColor: !form.subjectType ? '#C0392B' : undefined }} value={form.subjectType} onChange={async e => {
              const val = e.target.value;
              if (val === 'management') {
                setForm(prev => ({ ...prev, subjectType: val, majorDisputeFlag: true }));
                await saveMajorDisputeDraft();
              } else {
                  setForm(prev => ({
                    ...prev,
                    subjectType: val,
                    subjectCraftType: val === 'yardmaster' ? (prev.craft || '') : ''
                  }));
                }
            }}>
              <option value="">Select one...</option>
              <option value="yardmaster">{form.craft || DEFAULT_CRAFT}</option>
              <option value="other_craft">Other Craft (Agreement-Covered Employee)</option>
              <option value="management">Non-Covered / Management</option>
            </select>
          </div>

          {form.subjectType === 'yardmaster' && (
            <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#E8A020', textTransform: 'uppercase', marginBottom: 12 }}>{(form.craft || DEFAULT_CRAFT)} — Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Name</label>
                  <input aria-label="Name" className="form-input" placeholder="Full name" value={form.violatorName} onChange={e => set('violatorName', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Seniority Roster #</label>
                  <input aria-label="Seniority Roster #" className="form-input" placeholder="Roster number" value={form.violatorRosterNumber} onChange={e => set('violatorRosterNumber', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Seniority Date</label>
                  <input aria-label="Seniority Date" className="form-input" type="date" min="1975-01-01" max={TODAY_DATE} value={form.subjectSeniorityDate || ''} onChange={e => set('subjectSeniorityDate', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Job Symbol / Assignment</label>
                  <input aria-label="Job Symbol / Assignment" className="form-input" placeholder="e.g. 0600-1400, Y091 East End" value={form.violatorAssignment} onChange={e => set('violatorAssignment', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Hours of Assignment</label>
                  <input aria-label="Hours of Assignment" className="form-input" placeholder="e.g. 0700-1500" value={form.violatorHours || ''} onChange={e => set('violatorHours', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Assigned Rest Days</label>
                  <input aria-label="Assigned Rest Days" className="form-input" placeholder="e.g. Sat / Sun" value={form.violatorRestDays} onChange={e => set('violatorRestDays', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {form.subjectType === 'other_craft' && (
            <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#8AA4BF', textTransform: 'uppercase', marginBottom: 12 }}>Other Craft Employee(s) — Details</div>
              <div className="form-group">
                <label className="form-label">Craft / Classification</label>
                <select aria-label="Craft / Classification" className="form-select" value={form.subjectCraftType} onChange={e => set('subjectCraftType', e.target.value)}>
                  <option value="">Select craft...</option>
                  <option value="Conductor">Conductor</option>
                  <option value="Engineer">Engineer / Locomotive Engineer</option>
                  <option value="Clerk">Clerk</option>
                  <option value="Dispatcher">Train Dispatcher</option>
                  <option value="Mechanical">Mechanical Department Employee</option>
                  <option value="Signal">Signal Department Employee</option>
                  <option value="Maintenance">Maintenance of Way Employee</option>
                  <option value="Other">Other Agreement-Covered Employee</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Name(s)</label>
                <input aria-label="Name(s)" className="form-input" placeholder="Full name(s) — list all involved" value={form.violatorName} onChange={e => set('violatorName', e.target.value)} />
              </div>
            </div>
          )}

          {form.subjectType === 'management' && (
            <div style={{ background: '#2D0A0A', border: '2px solid #C0392B', borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 14, color: '#FF6B6B', fontWeight: 700, marginBottom: 8 }}>⛔ CLAIM FILING STOPPED</div>
              <div style={{ fontSize: 12, color: '#F4A0A0', lineHeight: 1.7, marginBottom: 12 }}>
                A non-covered / management employee cannot be the subject of a standard penalty time claim. This matter constitutes a potential <strong>Major Dispute</strong> under the Railway Labor Act and must be handled exclusively by your General Chairperson.
              </div>
              <div style={{ fontSize: 12, color: '#F4F6F8', lineHeight: 1.6, background: '#1A0A0A', padding: '10px 14px', borderRadius: 6 }}>
                <strong style={{ color: '#E8A020' }}>What happens next:</strong><br />
                1. {mdSave === 'saved' ? 'Your information is saved as a draft. The draft is private to you. Your Local Chairperson cannot see it.'
                  : mdSave === 'failed' ? <span style={{ color: '#FF6B6B' }}>Your information was NOT saved ({mdSaveError}). Write it down. <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 11 }} disabled={savingDraft} onClick={saveMajorDisputeDraft}>Try the save again</button></span>
                  : mdSave === 'saving' ? 'Saving your information as a draft...'
                  : 'Your information is not saved yet.'}<br />
                2. No one has been notified. Contact your Local Chairperson yourself, now.<br />
                3. <strong>Only the General Chairperson ({form.generalCommittee || 'your committee'}) has the authority to determine next steps.</strong><br />
                4. Your filing deadline still runs. Do not wait to hear from anyone.
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 8 }}>
            <label className="form-label">Other Persons Directly Involved</label>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 4 }}>Management personnel, supervisors, and others connected to the incident.</div>
            <input aria-label="Other Persons Directly Involved" className="form-input" placeholder="Names and titles — e.g. 'D. Kingsland, Trainmaster; R. Behrens, Director Operations'" value={form.personsInvolved} onChange={e => set('personsInvolved', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Witnesses</label>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 4 }}>Names and titles of anyone who witnessed the incident.</div>
            <input aria-label="Witnesses" className="form-input" placeholder="e.g. 'Dave Kingsland, Yardmaster; Robert Behrens, Trainmaster'" value={form.witnesses} onChange={e => set('witnesses', e.target.value)} />
          </div>
        </div>

        {/* COMBINED — REMEDY SOUGHT / VALUATION */}
        {form.subjectType !== 'management' && (
          <div className="card" style={{ opacity: isIdentifiersComplete ? 1 : 0.5, pointerEvents: isIdentifiersComplete ? 'auto' : 'none' }}>
            <div className="card-header">
              <div>
                <div className="card-title">Remedy Sought / Valuation</div>
                <div className="card-subtitle" style={{ color: '#E8A020', fontSize: 11, fontWeight: 600 }}>
                  PHASE 1 — State your remedy and calculate the claim amount
                </div>
              </div>
            </div>

            {/* Remedy Sought */}
            <div className="form-group">
              <label className="form-label">Remedy Sought *</label>
              <div style={{ fontSize: 11, color: '#FFFFFF', fontWeight: 700, marginBottom: 6 }}>
              Required by agreement. State the specific remedy — e.g. "Claimant requests payment of 8 hours at the overtime rate of $150.00 per hour, totaling $1,200.00, as compensation for the violation of Rule 18."
              </div>
              <textarea aria-label="Remedy Sought"
                className="form-textarea"
                placeholder="e.g. 'Claimant requests payment of [X] hours at the overtime rate, totaling $[amount], pursuant to Rule [X] of the applicable agreement.'"
                value={form.remedySought || ''}
                onChange={e => set('remedySought', e.target.value)}
                rows={2}
              />
            </div>

            <div style={{ borderTop: '1px solid #2E4A6B', margin: '12px 0' }} />

            {/* Valuation */}
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
                <input aria-label="Overtime Claim (checked = overtime rate applies)"
                  type="checkbox"
                  checked={form.isOvertimeClaim}
                  onChange={e => set('isOvertimeClaim', e.target.checked)}
                  style={{ accentColor: '#E8A020', width: 16, height: 16 }}
                />
                <span style={{ fontSize: 13, color: form.isOvertimeClaim ? '#E8A020' : '#8AA4BF', fontWeight: 700 }}>
                  Overtime Claim (checked = overtime rate applies)
                </span>
              </label>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Hourly Rate — Straight Time (S/T) *</label>
                  <input aria-label="Hourly Rate — Straight Time (S/T)"
                    className="form-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.hourlyRateST || ''}
                    onChange={e => set('hourlyRateST', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Hourly Rate — Overtime (O/T)</label>
                  <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#40C070' }}>
                    {form.hourlyRateST ? `$${(form.hourlyRateST * 1.5).toFixed(2)}` : 'Auto = S/T × 1.5'}
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Total Hours Claimed *</label>
                  <input aria-label="Total Hours Claimed"
                    className="form-input"
                    type="number"
                    step="0.5"
                    min="0"
                    value={form.totalHoursClaimed || ''}
                    onChange={e => set('totalHoursClaimed', parseFloat(e.target.value) || 0)}
                    placeholder="0.0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Amount Claimed — O/T</label>
                  <div style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '10px 14px', fontSize: 15, color: '#40C070', fontWeight: 700 }}>
                    {form.hourlyRateST && form.totalHoursClaimed
                      ? `$${(form.hourlyRateST * 1.5 * form.totalHoursClaimed).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : 'Auto-calculates'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => {
            if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost. Use Save as Draft to keep your work.')) {
              onNavigate('claims');
            }
          }}>Cancel</button>
          {form.subjectType !== 'management' && (
            <button
              className="btn btn-secondary"
              onClick={handleSaveDraft}
              disabled={savingDraft}
            >
              {savingDraft ? 'Saving...' : draftSaved ? '✓ Draft Saved' : '📋 Save as Draft'}
            </button>
          )}
          {form.subjectType === 'management' ? (
            <div style={{ fontSize: 12, color: '#FF6B6B', padding: '8px 12px', background: '#2D0A0A', borderRadius: 6, border: '1px solid #C0392B' }}>
              ⛔ Filing stopped — contact your Local Chairperson now
            </div>
          ) : form.violatorIsNonAgreement ? (
            <button className="btn btn-primary" style={{ background: '#C0392B' }} onClick={handleNonAgreementRoute}>
              {saving ? 'Routing...' : 'Route as Letter of Non-Acquiescence'}
            </button>
          ) : !isFormValid ? (
            <div style={{ textAlign: 'right' }}>
              <button className="btn btn-primary" disabled style={{ opacity: 0.4, cursor: 'not-allowed' }}>
                File Claim
              </button>
              <div style={{ fontSize: 10, color: '#FF6B6B', marginTop: 4 }}>
                {!form.claimantName ? '⚠ Claimant name required' :
                 !isIdentifiersComplete ? '⚠ Complete all identifiers above' :
                 !form.generalCommittee ? '⚠ General Committee not set — reselect Railroad' :
                 !isSubjectSelected ? '⚠ Select a Subject of Claim' :
                 !isPhase1Complete ? '⚠ Complete the subject details' :
                 !form.description ? '⚠ Description of event required' :
                 !form.incidentDate ? '⚠ Incident date required' :
                 dateError ? '\u26A0 ' + dateError :
                     !form.violationType ? '\u26A0 Violation type required' :
                     !form.incidentTime24 ? '\u26A0 Time of incident required' :
                     !form.remedySought ? '\u26A0 Remedy sought required' :
                     !(form.totalAmountST || form.totalAmountOT) ? '\u26A0 Claim amount required' :
                     '\u26A0 Complete required fields above'}
              </div>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Filing...' : 'File Claim'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
